import React from 'react';

import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {GetChartViewDataFrame_dataFrame_DataFrameNode as VisualizationStepEditorDataFrameNode} from 'analysis-tool/ui/main-view/__generated/GetChartViewDataFrame';
import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {createRenderExpandTableButton} from 'data-frames/toolbar/toolbar-actions/renderExpandTableButton';
import DataFrameTableWrapper from 'data-frames/wrapper/DataFrameTableWrapper';
import {PipelineStepType, PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import hasDownstreamFlagRowsStep from 'pipelines/hasDownstreamFlagRowsStep';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {SaveState} from 'ui-library/SaveStatusIndicator';

import AdditionalEditorSection from '../components/AdditionalEditorSection';
import AddTransformStepDropdown, {
  BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE,
  FIX_FAILED_TRANSFORMS_MESSAGE,
} from './AddTransformStepDropdown';
import VisualizationEditorSkeleton from './editor-sections/VisualizationEditorSkeleton';
import getDatasetViewErrorHandlingOptions from './getDatasetViewErrorHandlingOptions';
import {getTransformPills} from './TransformPillUtils';

export const DONE_BUTTON_TEXT = 'Save settings';

const visualizationConfigState = makeUseGlobalClientState<VisualizationConfig | null>(null);
const visualizationNameState = makeUseGlobalClientState<string | null>(null);

function renderPreviewDataFrames(
  state: {
    clientStateId: string;
    analysis: AnalysisGraphQL;
    viewSteps: Array<GetAnalysisPipelineStep>;
    visualizationType: VisualizationType | null;
  },
  dataFrameNode: VisualizationStepEditorDataFrameNode,
  isExpandedPreviewState: boolean,
  setIsExpandedPreviewState: (newState: boolean) => void
): JSX.Element {
  const {clientStateId, analysis, viewSteps, visualizationType} = state;

  const errorMessage = getDatasetViewErrorHandlingOptions(
    viewSteps,
    !analysis.permissions.writable,
    true // ignoreTerminalStepErrors
  );

  if (visualizationType !== VisualizationType.TABLE && errorMessage == null) {
    return (
      <DataFrameTableWrapper
        actionOrigin={ActionOrigin.ANALYSIS_TOOL}
        clientStateId={`${clientStateId}-table-preview`}
        dataFrame={dataFrameNode.dataFrameJson}
        dataFrameId={dataFrameNode.id}
        renderIsExcludedColumn={false}
        additionalElements={[
          createRenderExpandTableButton(isExpandedPreviewState, setIsExpandedPreviewState),
        ]}
      />
    );
  }

  return <></>;
}

function renderEditorSidebarChildren(
  state: {
    analysis: AnalysisGraphQL;
    viewSteps: Array<GetAnalysisPipelineStep>;
    canAddNewSteps: boolean;
    addNewStepsDisabledMessage?: string;
    inputDataFrameId: string;
    visualizationConfig: VisualizationConfig | null;
    visualizationType: VisualizationType | null;
  },
  dataFrameNode: VisualizationStepEditorDataFrameNode,
  invalidatePreview: () => void
): JSX.Element {
  const {
    analysis,
    viewSteps,
    inputDataFrameId,
    visualizationType,
    canAddNewSteps,
    addNewStepsDisabledMessage,
  } = state;

  return (
    <>
      <AdditionalEditorSection
        title="Filters and Transformations"
        addItemElement={
          <AddTransformStepDropdown
            viewSteps={viewSteps}
            dataFrameSpec={
              visualizationType === VisualizationType.TABLE
                ? dataFrameNode.dataFrameJson
                : dataFrameNode.randomSubsetDataFrameJson
            }
            inputDataFrameId={inputDataFrameId}
            analysis={analysis}
            canAddNewSteps={canAddNewSteps}
            addNewStepsDisabledMessage={addNewStepsDisabledMessage}
          />
        }
        pills={getTransformPills(analysis, viewSteps)}
        showTopBorder={visualizationType !== VisualizationType.TABLE}
      />
    </>
  );
}

export interface ChartViewStepEditorProps {
  analysis: AnalysisGraphQL;
  viewSteps: Array<GetAnalysisPipelineStep>;
  clientStateId: string;
  inputDataFrameId: string;
  handleSaveChart: (visualizationConfig: VisualizationConfig, visualizationName: string) => Promise<void>;
  initialVisualizationConfig: VisualizationConfig;
  initialVisualizationName: string;
}

export default function ChartViewStepEditor({
  analysis,
  viewSteps,
  clientStateId,
  handleSaveChart,
  inputDataFrameId,
  initialVisualizationConfig,
  initialVisualizationName,
}: ChartViewStepEditorProps): JSX.Element | null {
  const [nullableVisualizationConfig, setVisualizationConfig] =
    visualizationConfigState.useGlobalClientState(clientStateId);
  const [nullableVisualizationName, setVisualizationName] =
    visualizationNameState.useGlobalClientState(clientStateId);

  const visualizationConfig = nullableVisualizationConfig ?? initialVisualizationConfig;
  const visualizationType = getVisualizationType(visualizationConfig);
  const visualizationName = nullableVisualizationName ?? initialVisualizationName;

  const allStepsSucceeded = viewSteps
    .filter(
      (step) => ![PipelineStepType.CHART_VIEW, PipelineStepType.TABLE_VIEW].includes(step.definition.stepType)
    )
    .every((step) => step.statusCode === PipelineStepStatusCode.SUCCEEDED);
  const blockedByDownstream = hasDownstreamFlagRowsStep(analysis, viewSteps[viewSteps.length - 1]);

  const canAddNewSteps = allStepsSucceeded && !blockedByDownstream;
  const addNewStepsDisabledMessage =
    (!allStepsSucceeded ? FIX_FAILED_TRANSFORMS_MESSAGE : undefined) ??
    (blockedByDownstream ? BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE : undefined);

  function onChangeVisualizationName(name: string): void {
    setVisualizationName(name);
  }

  function onChangeVisualizationConfig(config: VisualizationConfig): void {
    setVisualizationConfig(config);
  }

  return (
    <VisualizationEditorSkeleton
      clientStateId={clientStateId}
      analysis={analysis}
      viewSteps={viewSteps}
      canAddNewSteps={canAddNewSteps}
      inputDataFrameId={inputDataFrameId}
      visualizationSaveState={SaveState.SAVED}
      visualizationName={visualizationName}
      onChangeVisualizationName={onChangeVisualizationName}
      visualizationConfig={visualizationConfig}
      onChangeVisualizationConfig={onChangeVisualizationConfig}
      renderFromChart={true}
      renderPreviewDataFrames={(inputDataFrame, isExpandedPreviewState, setIsExpandedPreviewState) =>
        renderPreviewDataFrames(
          {clientStateId, analysis, viewSteps, visualizationType},
          inputDataFrame,
          isExpandedPreviewState,
          setIsExpandedPreviewState
        )
      }
      sidebarProps={{
        onClickDoneButton: () => {
          assert(
            visualizationConfig != null,
            'Visualization config should be non-null or button should be disabled.'
          );
          return handleSaveChart(visualizationConfig, visualizationName).then(() => {
            setVisualizationConfig(null);
            setVisualizationName(null);
          });
        },
        doneButtonText: DONE_BUTTON_TEXT,
        doneButtonIsDisabled: visualizationConfig == null || visualizationName === '',
        enableAnalysisTab: false,
        enableAggregation: false,
        enableErrorBarColumn: true,
        enableFormatTabForLineChart: true,
      }}
      renderEditorSidebarChildren={(inputDataFrame, invalidatePreview) =>
        renderEditorSidebarChildren(
          {
            analysis,
            viewSteps,
            canAddNewSteps,
            addNewStepsDisabledMessage,
            inputDataFrameId,
            visualizationConfig,
            visualizationType,
          },
          inputDataFrame,
          invalidatePreview
        )
      }
    />
  );
}
